/**
 * Drawer Action Form Helper — canonical pattern for the
 * "submit a form inside the visit drawer" flow.
 *
 * Replaces ~95% of the boilerplate that used to live in every
 * ``templates/office/visits/partials/_*_drawer.html`` partial:
 *
 *   - prevent default submit, run validators
 *   - lock submit button + spinner
 *   - POST FormData with X-CSRFToken + X-Requested-With (+ Accept JSON
 *     when ``expectJson: true``)
 *   - on success: refresh schedule views + toast + close drawer +
 *     soft-reload the /visits/ list page
 *   - on error: show parsed error message inline + toast
 *   - in the ``finally``: restore the submit button
 *
 * The partial keeps a single ``<script>`` that calls
 * ``window.wireDrawerActionForm({...})`` and is therefore eligible to
 * leave the inline-JS allowlist once the visit drawer shell stops
 * relying on ``executeScripts``-style nested execution (Phase 4
 * Vague 3). For now, the partial stays in the allowlist but the
 * ~100 LOC of validation/submit/error logic disappears from each
 * template.
 *
 * == Usage ==
 *
 *   <script>
 *     window.wireDrawerActionForm({
 *       formId: 'drawerNoShowForm',
 *       submitBtnId: 'drawerNoShowSubmit',
 *       validators: [
 *         window.requiredFieldValidator({
 *           inputId: 'drawer_no_show_reason',
 *           errorId: 'drawerNoShowReasonError',
 *           message: '{{ t_reason_required|escapejs }}',
 *         }),
 *       ],
 *       successMessage: '{{ t_success|escapejs }}',
 *       errorMessage: '{{ t_error_noshow|escapejs }}',
 *     });
 *   </script>
 *
 * == Response shape ==
 *
 *   - ``expectJson: false`` (default) → server returns
 *     302/200 HTML on success, 200/400 HTML on validation error.
 *     The error message is parsed from the first
 *     ``.text-red-600, .text-red-800`` element in the response body.
 *
 *   - ``expectJson: true`` → server returns
 *     ``200 {success: true, message?}`` on success and
 *     ``400 {success: false, error, field_errors?}`` on validation
 *     error. The ``message`` field, if present, overrides
 *     ``successMessage`` for the success toast. The full JSON payload
 *     (including ``field_errors``) is attached to the thrown Error as
 *     ``error.payload`` so that an ``onError`` override can distribute
 *     per-field messages to inline ``<p>`` containers.
 *
 *   For ``expectJson: true``, ``response.redirected`` is deliberately
 *   NOT trusted — a Django ``redirect()`` returns 302 followed by a
 *   200 GET, and that used to fire the "false success toast on
 *   validation error" bug in mark-done.
 *
 * == ``onSuccess`` / ``onError`` overrides ==
 *
 *   By default we call ``defaultSuccessActions`` (refresh schedule
 *   views + toast + close drawer + soft-reload). To customise (e.g.
 *   skip the soft-reload, navigate elsewhere, etc.), pass
 *   ``onSuccess: (payload, helpers) => {...}``. Helpers expose
 *   ``defaultSuccessActions(message?)`` so callers can mix in extra
 *   work without re-implementing the defaults.
 */

const SPINNER_SVG = '<svg class="animate-spin h-4 w-4 mr-1 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>';

/**
 * Default post-success actions: refresh the calendar / team
 * schedule views, fire a success toast, close the visit drawer and,
 * if we are sitting on the /visits/ list page, soft-reload the page.
 */
function runDefaultSuccessActions(message) {
  if (window.scheduleCalendar) {
    window.scheduleCalendar.refetchEvents();
  }
  if (window.ScheduleHub?.refresh?.team) {
    window.ScheduleHub.refresh.team();
  }
  if (message && typeof window.showToast === 'function') {
    window.showToast(message, 'success');
  }
  if (typeof window.hideVisitDrawer === 'function') {
    window.hideVisitDrawer();
  }
  if (window.location.pathname.includes('/visits/') && typeof window._softReload === 'function') {
    window._softReload();
  }
}

/**
 * Parse an error message out of an HTML response body. Mirrors the
 * legacy behaviour: look for the first ``.text-red-600`` /
 * ``.text-red-800`` element (i.e. an error rendered by Django's form
 * field templates) and return its textContent. Falls back to the
 * provided ``defaultMsg`` when nothing is found.
 */
function parseErrorFromHtml(html, defaultMsg) {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const errorEl = doc.querySelector('.text-red-600, .text-red-800');
    return errorEl ? errorEl.textContent.trim() || defaultMsg : defaultMsg;
  } catch {
    return defaultMsg;
  }
}

/**
 * Show the inline error message inside the form. Tries, in order:
 *   1. The explicit ``errorContainerId``, if provided.
 *   2. The first ``[data-form-error-target]`` inside the form.
 *   3. No-op (the toast is the user-visible signal).
 */
function showFormError(form, errorContainerId, message) {
  let target = null;
  if (errorContainerId) {
    target = document.getElementById(errorContainerId);
  }
  if (!target && form) {
    target = form.querySelector('[data-form-error-target]');
  }
  if (target) {
    target.textContent = message;
    target.classList.remove('hidden');
  }
}

/**
 * Wire a drawer action form. See module docblock for usage.
 *
 * @param {object} config
 * @param {string} config.formId
 * @param {string} config.submitBtnId
 * @param {Array<() => boolean>} [config.validators]
 *        Validators invoked in order on submit. Returning a falsy
 *        value (``false`` / ``undefined`` is the convention) aborts
 *        the submission. Each validator is responsible for surfacing
 *        its own user-visible error.
 * @param {boolean} [config.expectJson=false]
 * @param {string}  [config.successMessage='']
 * @param {string}  [config.errorMessage='Operation failed']
 *        Fallback message when neither the JSON ``error`` field nor
 *        the HTML ``.text-red-600`` parsing yields anything.
 * @param {string}  [config.errorContainerId]
 *        Id of the element that should display the inline error
 *        message. If omitted, the helper falls back to the first
 *        ``[data-form-error-target]`` inside the form.
 * @param {(payload: any, helpers: {defaultSuccessActions: (msg?: string) => void}) => void}
 *        [config.onSuccess]
 * @param {(error: Error, helpers: {showFormError: (msg: string) => void}) => void}
 *        [config.onError]
 */
export function wireDrawerActionForm(config) {
  const {
    formId,
    submitBtnId,
    validators = [],
    expectJson = false,
    successMessage = '',
    errorMessage = 'Operation failed',
    errorContainerId = null,
    onSuccess = null,
    onError = null,
  } = config || {};

  const form = document.getElementById(formId);
  const submitBtn = document.getElementById(submitBtnId);
  if (!form || !submitBtn) {
    console.warn(`wireDrawerActionForm: missing form#${formId} or button#${submitBtnId}`);
    return null;
  }

  const submitBtnOriginalText = submitBtn.innerHTML;

  function lockButton() {
    submitBtn.disabled = true;
    submitBtn.innerHTML = SPINNER_SVG + submitBtnOriginalText;
  }

  function unlockButton() {
    submitBtn.disabled = false;
    submitBtn.innerHTML = submitBtnOriginalText;
  }

  async function handleSubmit(event) {
    event.preventDefault();

    for (const validator of validators) {
      if (typeof validator === 'function' && validator() === false) {
        return;
      }
    }

    lockButton();

    const formData = new FormData(form);
    const csrfToken = formData.get('csrfmiddlewaretoken');

    const headers = {
      'X-Requested-With': 'XMLHttpRequest',
    };
    if (csrfToken) {
      headers['X-CSRFToken'] = csrfToken;
    }
    if (expectJson) {
      headers.Accept = 'application/json';
    }

    const fetchOptions = {
      method: 'POST',
      body: formData,
      headers,
    };
    if (expectJson) {
      // Stop fetch from following 302s opaquely — we want to inspect
      // the status / payload ourselves to avoid the "false success
      // on Django redirect after validation error" bug.
      fetchOptions.redirect = 'manual';
    }

    try {
      const response = await fetch(form.action, fetchOptions);
      const successHelpers = {
        defaultSuccessActions: runDefaultSuccessActions,
      };

      if (expectJson) {
        const contentType = response.headers.get('Content-Type') || '';
        const isJson = contentType.includes('application/json');
        const payload = isJson ? await response.json().catch(() => null) : null;

        if (response.ok && payload && payload.success) {
          if (typeof onSuccess === 'function') {
            onSuccess(payload, successHelpers);
          } else {
            runDefaultSuccessActions(payload.message || successMessage);
          }
          return;
        }

        const msg = (payload && payload.error) || errorMessage;
        const err = new Error(msg);
        // Attach the raw JSON payload so ``onError`` callers can read
        // ``payload.field_errors`` and distribute per-field messages to
        // inline containers (see visit-cleaner-absent-drawer.js).
        err.payload = payload;
        throw err;
      }

      // Non-JSON: success when redirected or 2xx, error otherwise.
      if (response.redirected || response.ok) {
        if (typeof onSuccess === 'function') {
          onSuccess(null, successHelpers);
        } else {
          runDefaultSuccessActions(successMessage);
        }
        return;
      }

      const html = await response.text();
      throw new Error(parseErrorFromHtml(html, errorMessage));
    } catch (error) {
      console.error(`Drawer action error (form#${formId}):`, error);
      const errorHelpers = {
        showFormError: (msg) => showFormError(form, errorContainerId, msg),
      };
      if (typeof onError === 'function') {
        onError(error, errorHelpers);
      } else {
        showFormError(form, errorContainerId, error.message || errorMessage);
        if (typeof window.showToast === 'function') {
          window.showToast(error.message || errorMessage, 'error');
        }
      }
    } finally {
      unlockButton();
    }
  }

  form.addEventListener('submit', handleSubmit);
  return { form, submitBtn };
}

/**
 * Validator factory: required text/textarea field.
 *
 * Wires real-time error clearing on ``input`` so the message
 * disappears as soon as the user starts typing again. Returns a
 * function that, when called, validates the field and shows / hides
 * the error message accordingly.
 *
 * @param {object} opts
 * @param {string} opts.inputId
 * @param {string} opts.errorId
 * @param {string} opts.message  — text shown on the inline error element.
 * @returns {() => boolean}
 */
export function requiredFieldValidator({ inputId, errorId, message }) {
  const input = document.getElementById(inputId);
  const errorEl = document.getElementById(errorId);

  if (!input) {
    console.warn(`requiredFieldValidator: missing input#${inputId}`);
    return () => true;
  }

  if (errorEl) {
    input.addEventListener('input', () => {
      if (input.value.trim()) {
        errorEl.classList.add('hidden');
        input.classList.remove('border-red-500');
      }
    });
  }

  return function validate() {
    const value = (input.value || '').trim();
    if (!value) {
      if (errorEl) {
        errorEl.textContent = message;
        errorEl.classList.remove('hidden');
      }
      input.classList.add('border-red-500');
      input.focus();
      return false;
    }
    return true;
  };
}

/**
 * Expose the helpers as window globals so the per-partial
 * ``<script>`` can call them without an ES module import. The
 * partials are loaded into the visit drawer shell via fetch +
 * inline-script execution (Phase 4 Vague 3 will replace that
 * mechanism); until then, the simplest way for the partial to reach
 * our utility is the global namespace.
 *
 * Module-side imports (``import { wireDrawerActionForm } from
 * '../utils/drawer-action-form.js'``) keep working too — both call
 * sites resolve to the same closure.
 */
if (typeof window !== 'undefined') {
  window.wireDrawerActionForm = wireDrawerActionForm;
  window.requiredFieldValidator = requiredFieldValidator;
}
